/**
 * Storefront Events Bridge - Moon Moon Agency
 * Adapte le cart drawer MM au standard Shopify "Standard storefront events and actions"
 * (Spring '26, GA). Apps tierces / pixels / agents IA reagissent a notre panier sans hack DOM.
 *
 * - Events  : pont des CustomEvents internes `cart:*` vers les events standard `shopify:cart:*`,
 *   + emission de `shopify:product:view` sur les pages produit.
 * - Actions : la config (openCart/updateCart via .configure) est faite par un inline script
 *   AU-DESSUS de `content_for_header` dans theme.liquid (timing impose par Shopify, evite la
 *   race condition avec le code des apps). Ce module ne touche PAS aux actions.
 *
 * Doc : https://shopify.dev/docs/storefronts/themes/best-practices/standard-events-and-actions
 */

/*********************************************************************************************
 * 🔌 STOREFRONT EVENTS MANAGER
 */
const StorefrontEventsManager = {
	_initialized: false,

	/**
	 * Mapping CustomEvent interne MM → event DOM standard Shopify.
	 * On ne mappe que les points de CONFIRMATION API pour eviter les doublons :
	 * - `quantity-synced` couvre changement de quantite ET suppression (la suppression
	 *   passe par setQuantity(0) → updateQuantity → quantity-synced).
	 * - `add-to-cart-success` est le chemin API distinct de l'ajout.
	 * `item-removed` n'est volontairement PAS mappe (sinon double lines-update par suppression).
	 */
	eventMap: {
		'cart:quantity-synced': 'shopify:cart:lines-update',
		'cart:add-to-cart-success': 'shopify:cart:lines-update',
		'cart:discount-applied': 'shopify:cart:discount-update',
		'cart:discount-removed': 'shopify:cart:discount-update',
		'cart:api-error': 'shopify:cart:error',
		'cart:opened': 'shopify:cart:view',
	},

	init() {
		if (this._initialized) return; // Guard anti-double-init (hot reload, double chargement)
		this._initialized = true;

		this._bridgeEvents();
		this._emitPageViews();
		window.log('info', '🔌 [STOREFRONT_EVENTS]', 'Initialized');
	},

	/**
	 * Emet les events "view" standard au chargement, selon le type de page.
	 * Source : window.ShopifyAnalytics.meta (injecte par Shopify dans content_for_header).
	 * Navigation MM = full page load (prefetch passif via <link rel=prefetch>, pas de SPA),
	 * donc une emission a l'init suffit (pas de re-tir SPA a gerer).
	 *
	 * - shopify:page:view    → toute page (toujours, avec pageType)
	 * - shopify:product:view → page produit (meta.product present)
	 * - shopify:collection:view → page collection (pageType === 'collection')
	 */
	_emitPageViews() {
		const meta = window.ShopifyAnalytics?.meta;
		if (!meta) return;

		const pageType = meta.page?.pageType || null;

		// page:view — emis sur tout chargement
		document.dispatchEvent(new CustomEvent('shopify:page:view', {
			detail: { pageType },
			bubbles: true,
		}));

		// product:view — page produit
		if (meta.product) {
			document.dispatchEvent(new CustomEvent('shopify:product:view', {
				detail: { product: meta.product },
				bubbles: true,
			}));
		}

		// collection:view — page collection
		if (pageType === 'collection') {
			document.dispatchEvent(new CustomEvent('shopify:collection:view', {
				detail: { pageType, products: meta.products || null },
				bubbles: true,
			}));
		}
	},

	/**
	 * Pont event-to-event : reemet chaque event interne MM sous le nom standard Shopify.
	 * Les apps standard ecoutent `shopify:cart:lines-update` etc. sans connaitre notre nomenclature.
	 */
	_bridgeEvents() {
		Object.entries(this.eventMap).forEach(([internalEvent, standardEvent]) => {
			document.addEventListener(internalEvent, (e) => {
				document.dispatchEvent(new CustomEvent(standardEvent, {
					detail: e.detail?.data || {},
					bubbles: true,
				}));
			});
		});
	},
};

// Auto-init avec verification readyState
if (document.readyState === 'loading') {
	document.addEventListener('DOMContentLoaded', () => StorefrontEventsManager.init());
} else {
	StorefrontEventsManager.init();
}
